import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import { cn } from "@/lib/utils"
import { OrgRole } from "@/types/team"
import { IconCheck, IconShieldLock, IconUser } from "@tabler/icons-react"

interface RoleChooserProps {
  value: OrgRole
  onChange: (role: OrgRole) => void
  disabled?: boolean
}

const ROLES: {
  key: OrgRole
  Icon: typeof IconUser
  nameKey: "TEAM/ROLE_MEMBER" | "TEAM/ROLE_ADMIN"
  descKey: "TEAM/ROLE_MEMBER_HINT" | "TEAM/ROLE_ADMIN_HINT"
}[] = [
  {
    key: "member",
    Icon: IconUser,
    nameKey: "TEAM/ROLE_MEMBER",
    descKey: "TEAM/ROLE_MEMBER_HINT",
  },
  {
    key: "admin",
    Icon: IconShieldLock,
    nameKey: "TEAM/ROLE_ADMIN",
    descKey: "TEAM/ROLE_ADMIN_HINT",
  },
]

/**
 * Two-card segmented role chooser (Member / Admin). The selected card gets a
 * crisp yellow border + faint tint + a check chip in the corner; the other
 * stays neutral. Icon tiles give each option weight without relying on a muddy
 * fill for the selected state.
 */
export function RoleChooser({
  value,
  onChange,
  disabled = false,
}: RoleChooserProps) {
  const { translate } = useTranslation()

  return (
    <div className="grid grid-cols-2 gap-3" role="radiogroup">
      {ROLES.map(({ key, Icon, nameKey, descKey }) => {
        const selected = key === value
        return (
          <button
            key={key}
            type="button"
            role="radio"
            aria-checked={selected}
            disabled={disabled}
            onClick={() => !selected && onChange(key)}
            className={cn(
              "group relative flex flex-col gap-2 rounded-xl border p-4 text-left transition-all",
              "focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background",
              disabled && "cursor-not-allowed opacity-50",
              selected
                ? "border-primary bg-primary/6"
                : "border-border hover:border-muted-foreground/40 hover:bg-muted/40",
            )}
          >
            <div className="flex items-center justify-between">
              <span
                className={cn(
                  "flex h-8 w-8 items-center justify-center rounded-lg transition-colors",
                  selected
                    ? "bg-primary/15 text-primary"
                    : "bg-muted text-muted-foreground group-hover:text-foreground",
                )}
              >
                <Icon size={17} stroke={1.75} />
              </span>
              <span
                className={cn(
                  "flex h-5 w-5 items-center justify-center rounded-full border transition-colors",
                  selected
                    ? "border-primary bg-primary text-primary-foreground"
                    : "border-border bg-transparent text-transparent",
                )}
              >
                <IconCheck size={13} stroke={3} />
              </span>
            </div>
            <div>
              <span className="block text-sm font-semibold">
                {translate(nameKey)}
              </span>
              <span className="mt-0.5 block text-xs leading-snug text-muted-foreground">
                {translate(descKey)}
              </span>
            </div>
          </button>
        )
      })}
    </div>
  )
}
